import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { EMPTY, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { DataService } from 'src/app/modules/auth/services/data.service';
import { environment } from 'src/environments/environment';
import { isActiveMasterMergeFeature } from 'src/app/config/master-merge-feature.flags';
import { P } from 'src/app/config/portal-permissions.constants';
import { PermissionService } from 'src/app/core/permissions/permission.service';
import { GorevPanosuPermissionService } from '../gorev-panosu-permission.service';
import {
  ApiResponse,
  DashboardNewsItem,
  DashboardProject,
  DashboardTemplate,
} from '../models/dashboard-task.model';

@Injectable({
  providedIn: 'root',
})
export class DashboardTaskService {
  private readonly injector = inject(Injector);

  constructor(
    private dataService: DataService,
    private http: HttpClient,
  ) {}

  private assertProjeYonetimiWrite(): boolean {
    return this.injector
      .get(GorevPanosuPermissionService)
      .assertProjeYonetimiWrite();
  }

  getProjects() {
    if (!isActiveMasterMergeFeature('dashboardTask')) {
      return of([] as DashboardProject[]);
    }

    const permissionService = this.injector.get(PermissionService);
    const url = 'api/ProjectWorkflow/GetProjects';

    return permissionService.grantsReady.pipe(
      filter((ready) => ready),
      take(1),
      switchMap(() => {
        if (
          !permissionService.hasAnyRead(
            P.GorevPanosu.Module,
            P.GorevPanosu.ProjeYonetimi,
          )
        ) {
          return of([] as DashboardProject[]);
        }

        return this.dataService.get<ApiResponse<any> | any>(url).pipe(
          map((response) => {
            const root = response?.data ?? response;
            const list = Array.isArray(root)
              ? root
              : Array.isArray(root?.items)
                ? root.items
                : [];

            return (list || []).map((p: any) => ({
              projectId: String(p.projectId ?? p.projectID ?? p.id ?? ''),
              name: String(p.name ?? p.projectName ?? '-'),
              type: p.type ?? p.projectType ?? null,
              status: (p.status ?? 'Draft') as string,
              completionPercent: Number(
                p.completionPercent ?? p.completionPercentage ?? p.progress ?? 0,
              ),
              companyId: p.companyId ?? p.companyID ?? null,
              templateId:
                p.templateId ??
                p.templateID ??
                p.templateGuid ??
                p.templateGUID ??
                p.appliedTemplateId ??
                p.appliedTemplateID ??
                p.currentTemplateId ??
                null,
            })) as DashboardProject[];
          }),
        );
      }),
    );
  }

  /* getProjects_disabled() {
    const url = 'api/ProjectWorkflow/GetProjects';
    return this.dataService.get<ApiResponse<any> | any>(url).pipe(
      map((response) => {
        const root = response?.data ?? response;
        const list = Array.isArray(root)
          ? root
          : Array.isArray(root?.items)
            ? root.items
            : [];

        return (list || []).map((p: any) => ({
          projectId: String(p.projectId ?? p.projectID ?? p.id ?? ''),
          name: String(p.name ?? p.projectName ?? '-'),
          type: p.type ?? p.projectType ?? null,
          status: (p.status ?? 'Draft') as string,
          completionPercent: Number(
            p.completionPercent ?? p.completionPercentage ?? p.progress ?? 0,
          ),
          companyId: p.companyId ?? p.companyID ?? null,
          templateId:
            p.templateId ??
            p.templateID ??
            p.templateGuid ??
            p.templateGUID ??
            p.appliedTemplateId ??
            p.appliedTemplateID ??
            p.currentTemplateId ??
            null,
        })) as DashboardProject[];
      }),
    );
  } */

  createProject(name: string, type: string) {
    if (!this.assertProjeYonetimiWrite()) {
      return EMPTY;
    }
    const url = 'api/ProjectWorkflow/CreateProject';
    return this.dataService
      .post<ApiResponse<any> | any>(url, { name, type })
      .pipe(map((response) => response?.data ?? response ?? null));
  }

  getProjectById(projectId: string) {
    const url = `api/ProjectWorkflow/GetProjectById?projectId=${encodeURIComponent(projectId)}`;
    return this.dataService
      .get<ApiResponse<any> | any>(url)
      .pipe(map((response) => response?.data ?? response ?? null));
  }

  createTemplate(payload: {
    code: string;
    name: string;
    type: string;
    version: number;
  }) {
    if (!this.assertProjeYonetimiWrite()) {
      return EMPTY;
    }
    const url = 'api/ProjectWorkflow/CreateTemplate';
    return this.dataService
      .post<ApiResponse<any> | any>(url, payload)
      .pipe(map((response) => response?.data ?? response ?? null));
  }

  getTemplates(isActive: boolean | null = true) {
    const query =
      isActive === null || isActive === undefined
        ? ''
        : `?isActive=${isActive}`;
    const url = `api/ProjectWorkflow/GetProjectWorkflowTemplates${query}`;
    return this.dataService.get<ApiResponse<any> | any>(url).pipe(
      map((response) => {
        const root = response?.data ?? response;
        const list = Array.isArray(root)
          ? root
          : Array.isArray(root?.items)
            ? root.items
            : [];

        return (list || []).map((x: any) => ({
          id: String(x.id ?? ''),
          code: x.code ?? '',
          name: x.name ?? '-',
          type: x.type ?? null,
          version: Number(x.version ?? 1),
          isActive: !!x.isActive,
        })) as DashboardTemplate[];
      }),
    );
  }

  getTemplatesByType(type: string, isActive: boolean | null = true) {
    return this.getTemplates(isActive).pipe(
      map((templates) =>
        (templates || []).filter(
          (template) =>
            String(template.type ?? '').toLowerCase() ===
            String(type ?? '').toLowerCase(),
        ),
      ),
    );
  }

  applyTemplate(projectId: string, templateId: string) {
    if (!this.assertProjeYonetimiWrite()) {
      return EMPTY;
    }
    const url = 'api/ProjectWorkflow/ApplyTemplateToProject';
    return this.dataService
      .post<ApiResponse<any> | any>(url, { projectId, templateId })
      .pipe(map((response) => response?.data ?? response ?? null));
  }

  activateProject(projectId: string) {
    if (!this.assertProjeYonetimiWrite()) {
      return EMPTY;
    }
    const url = 'api/ProjectWorkflow/ActivateProject';
    return this.dataService
      .post<ApiResponse<any> | any>(url, { projectId })
      .pipe(map((response) => response?.data ?? response ?? null));
  }

  getBlogFeed() {
    const apiUrl = `${environment.apiUrl}/api/PublicContent/blog-feed`;
    return this.http
      .get(apiUrl, { responseType: 'text' })
      .pipe(map((xml) => this.parseBlogFeed(xml)));
  }

  private parseBlogFeed(xmlText: string): DashboardNewsItem[] {
    if (!xmlText) {
      return [];
    }
    const xml = new DOMParser().parseFromString(xmlText, 'application/xml');
    const parseError = xml.querySelector('parsererror');
    if (parseError) {
      return [];
    }

    const items = Array.from(xml.querySelectorAll('channel > item'));
    return items
      .map((item) => {
        const title = this.getNodeText(item, 'title');
        const description = this.getNodeText(item, 'description');
        const link = this.getNodeText(item, 'link');
        const pubDate = this.getNodeText(item, 'pubDate');
        const enclosure = item.querySelector('enclosure');
        const imageUrl = this.optimizeFeedImageUrl(
          enclosure?.getAttribute('url') ?? null,
        );
        return {
          title: title || '-',
          description: description || '',
          link: link || '',
          pubDate: pubDate || null,
          imageUrl,
        } as DashboardNewsItem;
      })
      .filter((x) => !!x.link);
  }

  /**
   * Wix feed görsellerini dashboard thumbnail boyutuna indirir.
   * Büyük PNG yerine daha düşük çözünürlük + kalite ile daha hızlı yüklenir.
   */
  private optimizeFeedImageUrl(url: string | null): string | null {
    if (!url) {
      return null;
    }
    if (!url.includes('/v1/fit/')) {
      return url;
    }
    return url.replace(
      /\/v1\/fit\/w_\d+,h_\d+,al_c,q_\d+\//,
      '/v1/fit/w_480,h_270,al_c,q_60/',
    );
  }

  private getNodeText(parent: Element, selector: string): string {
    const node = parent.querySelector(selector);
    return node?.textContent?.trim() ?? '';
  }
}
